import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
} from "@reown/appkit/react";
import { mainnet } from "@reown/appkit/networks";
import { createWalletClient, custom } from "viem";

import { usePublicClient } from "wagmi";
import {
  erc20Abi,
  formatEther as vFormatEther,
  formatUnits as vFormatUnits,
} from "viem";

import { auth } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseLoginFromWallet, firebaseLogout } from "../lib/walletLogin";

import {
  SUPPORTED_CHAINS,
  STABLE_BY_CHAIN,
  STABLE_DECIMALS_BY_CHAIN,
  USDC_BY_CHAIN,
  USDC_DECIMALS_BY_CHAIN,
  USDE_BY_CHAIN,
  USDE_DECIMALS_BY_CHAIN,
} from "../appRWAPromo/components/Portfolio/tokens";

const REQUIRED_CHAIN_ID = 1;

const norm = (a) => String(a || "").toLowerCase();

// ===== SIWE global lock + intent (to prevent multi-hook double-sign) =====
const SIWE_LOCK_KEY = "pp_siwe_lock";
const SIWE_INTENT_KEY = "pp_siwe_intent";

function tryAcquireSiweLock(address) {
  try {
    const now = Date.now();
    const cur = JSON.parse(localStorage.getItem(SIWE_LOCK_KEY) || "null");

    // if lock set < 15s ago => SIWE already running somewhere
    if (cur?.ts && now - cur.ts < 15000) return false;

    localStorage.setItem(
      SIWE_LOCK_KEY,
      JSON.stringify({ ts: now, addr: String(address || "").toLowerCase() })
    );
    return true;
  } catch {
    return true; // if storage not available - don't block
  }
}

function releaseSiweLock() {
  try {
    localStorage.removeItem(SIWE_LOCK_KEY);
  } catch {}
}

function setSiweIntent() {
  try {
    localStorage.setItem(SIWE_INTENT_KEY, "1");
  } catch {}
}

function consumeSiweIntent() {
  try {
    const v = localStorage.getItem(SIWE_INTENT_KEY);
    if (v === "1") {
      localStorage.setItem(SIWE_INTENT_KEY, "0");
      return true;
    }
    return false;
  } catch {
    return true;
  }
}


function normalizeChainId(chainId) {
  if (chainId == null) return null;
  if (typeof chainId === "number") return chainId;

  const s = String(chainId);
  if (s.includes(":")) {
    const maybe = Number(s.split(":").pop());
    return Number.isFinite(maybe) ? maybe : null;
  }
  if (s.startsWith("0x")) {
    const maybe = parseInt(s, 16);
    return Number.isFinite(maybe) ? maybe : null;
  }
  const maybe = Number(s);
  return Number.isFinite(maybe) ? maybe : null;
}

function getInjectedProvider() {
  if (typeof window === "undefined") return undefined;
  const eth = window.ethereum;
  if (!eth) return undefined;
  if (Array.isArray(eth.providers)) {
    return eth.providers.find((p) => p.isMetaMask) || eth.providers[0];
  }
  return eth;
}

// имя нативного токена по сети
function getNativeSymbol(chainId) {
  if (chainId === 56) return "BNB";
  if (chainId === 137) return "MATIC";
  return "ETH"; // 1, 42161 и т.п.
}

/**
 * Headless wallet auth hook:
 * - startWalletAuth(): открывает AppKit Connect и после connect авто-SIWE
 * - state: address/isConnected/balances/loading flags
 *
 * ВАЖНО: все долларовые балансы аккаунта идут с бэка (Portfolio / API),
 * этот хук — только про балансы кошелька (для модалки депозита и т.п.).
 */
export function useWalletAuth() {
  const { open } = useAppKit();
  const { address, isConnected, status } = useAppKitAccount({
    namespace: "eip155",
  });
  const { chainId } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider("eip155");

  const injected = getInjectedProvider();
  const provider = walletProvider || injected;

  const chainIdNum = useMemo(() => normalizeChainId(chainId), [chainId]);
  const isBusy = status === "connecting" || status === "reconnecting";


  // Активная сеть кошелька для балансов (аутентификация по-прежнему через mainnet)
  const activeChainId = useMemo(
    () => normalizeChainId(chainId) ?? REQUIRED_CHAIN_ID,
    [chainId]
  );

  const nativeSymbol = useMemo(
    () => getNativeSymbol(activeChainId),
    [activeChainId]
  );

  // viem publicClient по активной сети (1 / 56 / 137 / 42161 ...)
  const publicClient = usePublicClient(
    activeChainId ? { chainId: activeChainId } : undefined
  );

  const [fbUser, fbLoading] = useAuthState(auth);
  const isAuthed = !!fbUser;

  const isEmailAuth = !!fbUser?.providerData?.some(
    (p) => p?.providerId === "password" || p?.providerId === "emailLink"
  );

  // verify email только для email/password или emailLink
  const mustVerifyEmail = isEmailAuth && !fbUser?.emailVerified;

  const [pendingVerify, setPendingVerify] = useState(false);
  const autoStartedRef = useRef(false);
  const inFlight = useRef(false);
  const autoLinkRef = useRef(false);

  // Балансы кошелька по активной сети
  const [ethBalance, setEthBalance] = useState("0.00");
  const [usdtBalance, setUsdtBalance] = useState("0.00");
  const [usdcBalance, setUsdcBalance] = useState("0.00");
  const [usdeBalance, setUsdeBalance] = useState("0.00");
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // не делаем signOut email-юзера, если он просто дисконнектнул кошелек
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    const was = wasConnectedRef.current;
    if (was && !isConnected) {
      const method = localStorage.getItem("pp_auth_method"); // "wallet" | "email"
      if (method === "wallet") firebaseLogout();
      setPendingVerify(false);
      autoStartedRef.current = false;
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected]);

  const signMessageAsync = useCallback(
    async ({ message }) => {
      if (!provider) throw new Error("No provider yet");
      if (!address) throw new Error("No address");

      const client = createWalletClient({
        chain: mainnet,
        transport: custom(provider),
      });

      return await client.signMessage({
        account: address,
        message: String(message).replace(/\r\n/g, "\n"),
      });
    },
    [provider, address]
  );

  const openAccount = useCallback(() => {
    open?.({ view: "Account" });
  }, [open]);

  const openNetworks = useCallback(() => {
  open?.({ view: "Networks" });
}, [open]);


  const verifyAndLoginFirebase = useCallback(async () => {
    if (inFlight.current) return;
    if (fbLoading) return;
    if (!isConnected || !address) return;
    if (!provider) return;

    const method =
      typeof window !== "undefined"
        ? localStorage.getItem("pp_auth_method")
        : null;
    const lastWallet =
      typeof window !== "undefined"
        ? localStorage.getItem("pp_wallet")
        : null;

    // Если уже залогинены через кошелёк этим же адресом — не триггерим SIWE повторно
    if (
      isAuthed &&
      method === "wallet" &&
      lastWallet &&
      norm(lastWallet) === norm(address)
    ) {
      setPendingVerify(false);
      autoStartedRef.current = false;
      return;
    }

    inFlight.current = true;
    try {
      // Если метод был "wallet", а адрес сменился — выходим из старой сессии
      if (method === "wallet" && lastWallet && norm(lastWallet) !== norm(address)) {
        await firebaseLogout();
      }

      const result = await firebaseLoginFromWallet({
        address,
        chainId: REQUIRED_CHAIN_ID,
        signMessageAsync,
      });

      if (!isAuthed && (method === "wallet" || method == null)) {
        if (typeof window !== "undefined") {
          localStorage.setItem("pp_auth_method", "wallet");
          localStorage.setItem("pp_wallet", norm(address));
        }
      }

      void result;
    } finally {
      inFlight.current = false;
      setPendingVerify(false);
      autoStartedRef.current = false;
    }
  }, [address, isConnected, provider, signMessageAsync, fbLoading, isAuthed]);

 


  useEffect(() => {
    if (!pendingVerify) return;
    if (fbLoading) return;

    if (!isConnected || !address) return;
    if (!provider) return;
    if (isBusy) return;

    if (autoStartedRef.current) return;
    autoStartedRef.current = true;

    queueMicrotask(async () => {
  // 1) SIWE запускаем только если есть явный user-intent (Connect/Deposit)
  if (!consumeSiweIntent()) {
    setPendingVerify(false);
    autoStartedRef.current = false;
    return;
  }

  // 2) Глобальный лок на 15 секунд (между разными инстансами useWalletAuth)
  if (!tryAcquireSiweLock(address)) {
    setPendingVerify(false);
    autoStartedRef.current = false;
    return;
  }

  try {
    await verifyAndLoginFirebase();
  } finally {
    releaseSiweLock();
  }
});

  }, [
    pendingVerify,
    fbLoading,
    isConnected,
    address,
    provider,
    isBusy,
    verifyAndLoginFirebase,
  ]);

  // auto-link: email-пользователь подключил кошелёк → один раз линкуем его через SIWE
  useEffect(() => {
    const method =
      typeof window !== "undefined"
        ? localStorage.getItem("pp_auth_method")
        : null;

    if (!fbUser || method !== "email") {
      autoLinkRef.current = false;
      return;
    }

    const linkKey =
      typeof window !== "undefined" && address
        ? `pp_wallet_linked:${fbUser.uid}:${norm(address)}`
        : null;

    const alreadyLinkedInThisBrowser =
      !!linkKey &&
      typeof window !== "undefined" &&
      localStorage.getItem(linkKey) === "1";

    const shouldAutoLink =
      fbUser.emailVerified &&
      isConnected &&
      !!address &&
      !!provider &&
      !isBusy &&
      !fbLoading &&
      !mustVerifyEmail &&
      !alreadyLinkedInThisBrowser;

    if (!shouldAutoLink) {
      autoLinkRef.current = false;
      return;
    }

    if (autoLinkRef.current) return;
    autoLinkRef.current = true;

    queueMicrotask(async () => {
  // auto-link must also be single-run across multiple hook instances
  if (!tryAcquireSiweLock(address)) {
    autoLinkRef.current = false;
    return;
  }

  try {
    await verifyAndLoginFirebase();
    if (linkKey && typeof window !== "undefined") {
      localStorage.setItem(linkKey, "1");
    }
  } finally {
    releaseSiweLock();
    autoLinkRef.current = false;
  }
});

  }, [
    fbUser,
    fbLoading,
    isConnected,
    address,
    provider,
    isBusy,
    mustVerifyEmail,
    verifyAndLoginFirebase,
  ]);

  // ===== МУЛЬТИЧЕЙН БАЛАНСЫ КОШЕЛЬКА (native + USDT + USDC + USDe) =====

  const balInFlight = useRef(false);
  const hadBalancesRef = useRef(false);

  useEffect(() => {

    const isTabActive = () =>
    typeof document !== "undefined" ? document.visibilityState === "visible" : true;
    

    let stopped = false;
    let timer = null;

    const shouldShow =
      !!publicClient &&
      isConnected &&
      !!address &&
      !isBusy &&
      !!activeChainId &&
      SUPPORTED_CHAINS.includes(Number(activeChainId));

    hadBalancesRef.current = false;

    const fetchOnce = async () => {
      if (stopped) return;
      if (balInFlight.current) return;
      balInFlight.current = true;

      const showLoading = !hadBalancesRef.current;
      if (showLoading) setIsLoadingBalance(true);

      try {
        if (!activeChainId || !publicClient || !address) {
          setEthBalance("0.00");
          setUsdtBalance("0.00");
          setUsdcBalance("0.00");
          setUsdeBalance("0.00");
          hadBalancesRef.current = true;
          return;
        }

        // 1) Нативный токен (ETH / BNB / MATIC / ...)
        const nativeWei = await publicClient.getBalance({ address });

        // 2) USDT (каноничный по сети)
        const usdtAddr = STABLE_BY_CHAIN[activeChainId];
        let usdtStr = "0.00";

        if (usdtAddr) {
          const usdtRaw = await publicClient.readContract({
            address: usdtAddr,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [address],
          });

          const usdtDec =
            STABLE_DECIMALS_BY_CHAIN[activeChainId] ??
            STABLE_DECIMALS_BY_CHAIN[1] ??
            6;

          usdtStr = Number(vFormatUnits(usdtRaw, usdtDec)).toFixed(2);
        }

        // 3) USDC (каноничный по сети)
        const usdcAddr = USDC_BY_CHAIN[activeChainId];
        let usdcStr = "0.00";

        if (usdcAddr) {
          const usdcRaw = await publicClient.readContract({
            address: usdcAddr,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [address],
          });

          const usdcDec =
            USDC_DECIMALS_BY_CHAIN[activeChainId] ??
            USDC_DECIMALS_BY_CHAIN[1] ??
            6;

          usdcStr = Number(vFormatUnits(usdcRaw, usdcDec)).toFixed(2);
        }

        // 4) USDe (в allowlist пока только Ethereum)
        const usdeAddr = USDE_BY_CHAIN[activeChainId];
        let usdeStr = "0.00";

        if (usdeAddr) {
          const usdeRaw = await publicClient.readContract({
            address: usdeAddr,
            abi: erc20Abi,
            functionName: "balanceOf",
            args: [address],
          });

          const usdeDec =
            USDE_DECIMALS_BY_CHAIN[activeChainId] ??
            USDE_DECIMALS_BY_CHAIN[1] ??
            18;

          usdeStr = Number(vFormatUnits(usdeRaw, usdeDec)).toFixed(2);
        }

        if (stopped) return;

        setEthBalance(Number(vFormatEther(nativeWei)).toFixed(4));
        setUsdtBalance(usdtStr);
        setUsdcBalance(usdcStr);
        setUsdeBalance(usdeStr);

        hadBalancesRef.current = true;
      } catch (e) {
        console.warn("[balances] viem failed:", e && e.message ? e.message : e);
        setEthBalance("0.00");
        setUsdtBalance("0.00");
        setUsdcBalance("0.00");
        setUsdeBalance("0.00");
        hadBalancesRef.current = true;
      } finally {
        balInFlight.current = false;
        if (!stopped && showLoading) setIsLoadingBalance(false);
      }
    };

    if (!shouldShow) {
      setEthBalance("0.00");
      setUsdtBalance("0.00");
      setUsdcBalance("0.00");
      setUsdeBalance("0.00");
      setIsLoadingBalance(false);
      return;
    }

   fetchOnce();
timer = setInterval(() => {
  if (!isTabActive()) return;
  fetchOnce();
}, 45000);


    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      balInFlight.current = false;
    };
  }, [publicClient, isConnected, address, activeChainId, isBusy]);

  // объект балансов для фронта (модалка депозита и т.п.)
  const balances = useMemo(() => {
    const nativeNum = Number(ethBalance) || 0;
    const usdtNum = Number(usdtBalance) || 0;
    const usdcNum = Number(usdcBalance) || 0;
    const usdeNum = Number(usdeBalance) || 0;

    return {
      [nativeSymbol]: {
        amount: ethBalance,
        amountNum: nativeNum,
      },
      USDT: {
        amount: usdtBalance,
        amountNum: usdtNum,
      },
      USDC: {
        amount: usdcBalance,
        amountNum: usdcNum,
      },
      USDe: {
        amount: usdeBalance,
        amountNum: usdeNum,
      },
    };
  }, [ethBalance, usdtBalance, usdcBalance, usdeBalance, nativeSymbol]);

  // просто подключить кошелёк (без SIWE)
  const connectWalletOnly = useCallback(() => {
    if (mustVerifyEmail) return;

    setPendingVerify(false);
    autoStartedRef.current = false;

    if (isConnected) {
      open?.({ view: "Account" });
      return;
    }
    open?.({ view: "Connect" });
  }, [open, isConnected, mustVerifyEmail]);

  // единственная публичная точка старта wallet auth
  const startWalletAuth = useCallback(async () => {
  if (mustVerifyEmail) return;

  setPendingVerify(true);
  autoStartedRef.current = false;

  // global intent: even if startWalletAuth called from 2 components, SIWE should happen once
  setSiweIntent();

  // If already connected: open Account, SIWE will run via pendingVerify effect below
  if (isConnected) {
    open?.({ view: "Account" });
    return;
  }

  open?.({ view: "Connect" });
}, [open, isConnected, mustVerifyEmail]);


  return {
    // appkit/wallet state
    address,
    isConnected,
    chainIdNum,
    isBusy,
    connectWalletOnly,

    // firebase
    isAuthed,
    fbLoading,
    mustVerifyEmail,

    // balances кошелька (для модалки, не для аккаунт-баланса)
    ethBalance,
    usdtBalance,
    usdcBalance,
    usdeBalance,
    isLoadingBalance,
    balances,

    // auth control
    pendingVerify,
    startWalletAuth,
    openAccount,
    openNetworks,
  };
}
